import { Injectable } from '@angular/core';
import { Params } from '@angular/router';

import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { SortInfo } from '@stylehaven/components';
import { ApiFragment } from '../../record/entities/api-fragment.entity';
import { PaginatedResults } from '../../record/entities/pagination/paginated-results.entity';
import { PaginationPreferences } from '../../record/entities/pagination/pagination-preferences.entity';
import { RecordSearch } from '../../record/entities/record-search.entity';
import { AddPageToCache, InitializePageCache } from '../../state/overview-page-cache/overview-page-cache.actions';
import { OverviewPageCache, RecordIndex } from '../../state/overview-page-cache/overview-page-cache.entity';
import { getOverviewPageCache } from '../../state/overview-page-cache/overview-page-cache.selector';
import { Clear, Initialize, Pop, Push } from '../../state/overview-stack/overview-stack.actions';
import {
    SetBackToSearchLabel,
    SetExpandedIds,
    SetPaginationPreferences,
    SetQueryParams,
    SetRecordSearch,
    SetSort
} from '../../state/overview/overview-options.actions';
import { OverviewOptions } from '../../state/overview/overview-options.entity';
import { getOverviewOptions } from '../../state/overview/overview-options.selector';
import { SharedApplicationState } from '../../state/shared-state';

@Injectable()
export class OverviewOptionsService {
    constructor(private store: Store<SharedApplicationState>) {}

    public getOverviewOptions(overviewId: string): Observable<OverviewOptions | undefined> {
        if (overviewId === '') {
            return of(undefined);
        }
        return this.store.select(getOverviewOptions(overviewId)).pipe(
            catchError(() => {
                // Do nothing on error to prevent error in applications where shared application state isn't implemented.
                console.warn('Could not retrieve options for overview', overviewId, 'from store.');
                return of(undefined);
            })
        );
    }

    public getOverviewPageCache(overviewId: string): Observable<OverviewPageCache | undefined> {
        if (overviewId === '') {
            return of(undefined);
        }
        return this.store.select(getOverviewPageCache(overviewId)).pipe(
            catchError(() => {
                // Do nothing on error to prevent error in applications where shared application state isn't implemented.
                console.warn('Could not retrieve cache for overview', overviewId, 'from store.');
                return of(undefined);
            })
        );
    }

    public setPaginationPreferences(view: string, paginationPreferences: PaginationPreferences): void {
        this.store.dispatch(new SetPaginationPreferences(view, paginationPreferences));
    }

    public setSort(view: string, sort: SortInfo | undefined): void {
        this.store.dispatch(new SetSort(view, sort));
    }

    public setQueryParams(view: string, queryParams: Params | undefined): void {
        this.store.dispatch(new SetQueryParams(view, queryParams));
    }

    public initializePageCache(view: string, index: number, page: PaginatedResults<ApiFragment>): void {
        this.store.dispatch(new InitializePageCache(view, index, page));
    }

    public addPageToCache(view: string, index: number, page: PaginatedResults<ApiFragment>): void {
        this.store.dispatch(new AddPageToCache(view, index, page));
    }

    public getIndexOfRecord(overviewId: string, recordId: string): Observable<RecordIndex | undefined> {
        return this.getOverviewPageCache(overviewId).pipe(map(cache => cache?.recordsPageLookup?.[recordId]));
    }

    public setRecordSearch(view: string, recordSearch: RecordSearch): void {
        this.store.dispatch(new SetRecordSearch(view, recordSearch));
    }

    public clearStack(): void {
        this.store.dispatch(new Clear());
    }

    public pushToStack(overview: string, topLevelOverview: boolean): void {
        if (!overview) {
            return;
        }

        if (topLevelOverview) {
            this.store.dispatch(new Initialize(overview));
        } else {
            this.store.dispatch(new Push(overview));
        }
    }

    public clearStackUpTo(overview?: string): void {
        this.store.dispatch(new Pop(overview));
    }

    public peekStack(overviewContextToIgnore?: string): Observable<OverviewOptions | undefined> {
        return this.store
            .select(state => state.overviewStack.stack)
            .pipe(
                switchMap(stack => {
                    if (!stack || stack.length === 0) {
                        return of(undefined);
                    }

                    const filteredOverviews = stack.filter(overview => overview !== overviewContextToIgnore);
                    const overviewId = filteredOverviews[filteredOverviews.length - 1];
                    return this.getOverviewOptions(overviewId);
                })
            );
    }

    public setBackToSearchLabel(overviewId: string, backToSearchLabel: string): void {
        this.store.dispatch(new SetBackToSearchLabel(overviewId, backToSearchLabel));
    }

    public setExpandedIds(view: string, expandedIds: string[]): void {
        if (!view) {
            return;
        }

        this.store.dispatch(new SetExpandedIds(view, expandedIds));
    }
}
